import { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PMAvatar, PMCard, PMDataTable, PMMenu, PMSegmented, PMSkeleton } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { usePeople } from "@/data/queries";
import { Page } from "@/ui/Page";
import { Kpi } from "@/ui/Kpi";
import { Character } from "@/ui/Character";
import { avatarColor, clock, dayKey, fullName, relTime } from "@/ui/format";
import { OTHER, seriesColor, StackedColumns, type StackCol } from "./charts";
import { useActivity } from "./queries";
import "./dashboards.css";

const DAY = 86_400_000;
const RANGES = { day: { label: "Day", days: 1 }, week: { label: "Week", days: 7 }, month: { label: "Month", days: 30 } } as const;
type Range = keyof typeof RANGES;

// Team activity (core-features §4.3): who did what, filtered by day, week or month.
export function TeamActivityPage() {
  const search = useSearch({ from: "/t/$teamId/dashboards/team" });
  const nav = useNavigate();
  const { teamId } = useApp();
  const people = usePeople();
  const range: Range = (search.range as Range) in RANGES ? (search.range as Range) : "week";
  const set = (patch: Record<string, string | undefined>) => nav({ to: ".", search: { ...search, ...patch }, replace: true } as any);

  // Period boundaries: day = since midnight; week/month = rolling.
  const { start, prevStart } = useMemo(() => {
    const now = new Date();
    const s = range === "day" ? new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() : now.getTime() - RANGES[range].days * DAY;
    const len = now.getTime() - s || DAY;
    return { start: s, prevStart: s - Math.max(len, range === "day" ? DAY : len) };
  }, [range]);
  const act = useActivity(new Date(prevStart).toISOString());

  const who = search.who;
  const inCur = (iso?: string | null) => !!iso && new Date(iso).getTime() >= start;
  const inPrev = (iso?: string | null) => !!iso && new Date(iso).getTime() >= prevStart && new Date(iso).getTime() < start;
  const mine = <T,>(rows: T[], key: (r: T) => string | null) => (who ? rows.filter((r) => key(r) === who) : rows);
  const visits = mine(act.visits, (v) => v.user_id);
  const signs = mine(act.signs, (s) => s.user_id);
  const tasks = mine(act.tasks, (t) => t.completed_by_id || t.assigned_user_id);

  const cur = {
    visits: visits.filter((v) => inCur(v.started_at)),
    obs: visits.filter((v) => inCur(v.started_at)).reduce((a, v) => a + v.obs, 0),
    signs: signs.filter((s) => inCur(s.approved_at)),
    tasks: tasks.filter((t) => inCur(t.completed_at)),
  };
  const prev = {
    visits: visits.filter((v) => inPrev(v.started_at)).length,
    obs: visits.filter((v) => inPrev(v.started_at)).reduce((a, v) => a + v.obs, 0),
    signs: signs.filter((s) => inPrev(s.approved_at)).length,
    tasks: tasks.filter((t) => inPrev(t.completed_at)).length,
  };
  const delta = (c: number, p: number) => (p ? Math.round(((c - p) / p) * 100) : null);

  // Series: top 4 advisors by visits in the period keep fixed colors; the rest fold into "Other".
  const ranked = (() => {
    const n = new Map<string, number>();
    for (const v of cur.visits) if (v.user_id) n.set(v.user_id, (n.get(v.user_id) || 0) + 1);
    return people.data.map((p) => p.user_id).sort((a, b) => (n.get(b) || 0) - (n.get(a) || 0) || fullName(people.data.find((p) => p.user_id === a)).localeCompare(fullName(people.data.find((p) => p.user_id === b))));
  })();
  const shown = (who ? [who] : ranked).slice(0, 4);
  const hasOther = !who && ranked.length > 4;
  const series = [
    ...shown.map((id, i) => ({ id, label: fullName(people.data.find((p) => p.user_id === id)), color: seriesColor(i) })),
    ...(hasOther ? [{ id: "__other", label: "Other", color: OTHER }] : []),
  ];

  const cols: StackCol[] = (() => {
    const idx = (uid: string | null) => { const i = series.findIndex((s) => s.id === uid); return i >= 0 ? i : hasOther ? series.length - 1 : -1; };
    if (range === "day") {
      const hours = Array.from({ length: 15 }, (_, i) => i + 6);
      return hours.map((h) => {
        const vals = series.map(() => 0);
        for (const v of cur.visits) if (v.started_at && new Date(v.started_at).getHours() === h) { const i = idx(v.user_id); if (i >= 0) vals[i]++; }
        return { key: String(h), label: new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: "numeric" }), values: vals };
      });
    }
    const n = RANGES[range].days;
    return Array.from({ length: n }, (_, k) => {
      const d = dayKey(new Date(Date.now() - (n - 1 - k) * DAY).toISOString());
      const vals = series.map(() => 0);
      for (const v of cur.visits) if (dayKey(v.started_at) === d) { const i = idx(v.user_id); if (i >= 0) vals[i]++; }
      const date = new Date(d + "T12:00:00");
      return { key: d, label: n <= 7 ? date.toLocaleDateString(undefined, { weekday: "short" }) : date.toLocaleDateString(undefined, { month: "short", day: "numeric" }), values: vals };
    });
  })();

  const rows = people.data.map((p) => {
    const v = cur.visits.filter((x) => x.user_id === p.user_id);
    const last = [...act.visits.filter((x) => x.user_id === p.user_id).map((x) => x.completed_at || x.started_at), ...act.signs.filter((x) => x.user_id === p.user_id).map((x) => x.approved_at)].filter(Boolean).sort().at(-1) ?? null;
    return { id: p.user_id, name: fullName(p), visits: v.length, obs: v.reduce((a, x) => a + x.obs, 0), signed: cur.signs.filter((x) => x.user_id === p.user_id).length, tasks: cur.tasks.filter((x) => (x.completed_by_id || x.assigned_user_id) === p.user_id).length, last };
  }).filter((r) => !who || r.id === who);

  type Ev = { id: string; at: string; kind: "visit" | "sign" | "task"; uid: string | null; text: string; where: string | null; visitId: string | null };
  const events: Ev[] = [
    ...cur.visits.map((v) => ({ id: "v" + v.id, at: v.completed_at || v.started_at!, kind: "visit" as const, uid: v.user_id, text: v.completed_at ? "completed a field visit" : "started a field visit", where: v.location_label, visitId: v.id })),
    ...cur.signs.map((s) => ({ id: "s" + s.id, at: s.approved_at, kind: "sign" as const, uid: s.user_id, text: "signed the report", where: s.location_label, visitId: s.field_note_id })),
    ...cur.tasks.map((t) => ({ id: "t" + t.id, at: t.completed_at, kind: "task" as const, uid: t.completed_by_id || t.assigned_user_id, text: `completed “${t.details}”`, where: t.location_label, visitId: t.field_note_id })),
  ].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 10);

  const period = range === "day" ? "today" : range === "week" ? "the last 7 days" : "the last 30 days";
  const prevLabel = range === "day" ? "vs yesterday" : `vs previous ${RANGES[range].days} days`;
  const openVisit = (id: string) => nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: id } });
  const whoName = who ? fullName(people.data.find((p) => p.user_id === who)) : null;

  return (
    <Page
      chip="DASHBOARDS"
      title="Team activity"
      subtitle={`${whoName ?? "Everyone"} · ${period} · ${prevLabel}`}
      wide
      actions={
        <div className="range-row">
          <PMMenu label={whoName ?? "All advisors"} icon="user" searchable value={who ?? ""} align="right"
            options={[{ value: "", label: "All advisors" }, ...people.data.map((p) => ({ value: p.user_id, label: fullName(p) }))]}
            onChange={(v: string | string[]) => set({ who: (v as string) || undefined })} />
          <PMSegmented value={range} onChange={(v: string) => set({ range: v === "week" ? undefined : v })} options={Object.entries(RANGES).map(([value, r]) => ({ value, label: r.label }))} />
        </div>
      }
    >
      <div className="kpis">
        <Kpi emoji="📋" tone="tone-brand" value={cur.visits.length} label="Field visits" delta={delta(cur.visits.length, prev.visits)} />
        <Kpi emoji="🔎" tone="tone-photo" value={cur.obs} label="Observations" delta={delta(cur.obs, prev.obs)} />
        <Kpi emoji="✍️" tone="tone-text" value={cur.signs.length} label="Reports signed" delta={delta(cur.signs.length, prev.signs)} />
        <Kpi emoji="✅" tone="tone-warn" value={cur.tasks.length} label="Tasks completed" delta={delta(cur.tasks.length, prev.tasks)} />
      </div>

      {act.loading && !act.visits.length ? <PMSkeleton shape="rect" height={320} radius={16} /> : (
        <div className="dash-grid">
          <PMCard eyebrow={range === "day" ? "Visits by hour" : "Visits by day"} icon="bar-chart-outline" className="dash-span2">
            {cur.visits.length ? (
              <StackedColumns cols={cols} series={series} height={260}
                summary={`Field visits ${period}: ${cur.visits.length} total. ${series.map((s, i) => `${s.label} ${cols.reduce((a, c) => a + (c.values[i] || 0), 0)}`).join(", ")}.`} />
            ) : <Character name="car" size={110} title="No visits in this period" line="Pick a longer range, or check back when the team is in the field." />}
          </PMCard>

          <PMCard eyebrow="By advisor" icon="user-outline" count={rows.length} className="dash-span2">
            <PMDataTable rowKey="id" rows={rows} onRowClick={(r: any) => set({ who: who === r.id ? undefined : r.id })}
              columns={[
                { key: "name", label: "Advisor", sortable: true, render: (r: any) => { const si = series.findIndex((x) => x.id === r.id); const sw = si >= 0 ? series[si].color : hasOther ? OTHER : null; return <span className="row">{sw && <i className="dv-swatch" style={{ background: sw }} title="Chart color" />}<PMAvatar name={r.name} size={28} color={avatarColor(r.id)} /><strong>{r.name}</strong></span>; } },
                { key: "visits", label: "Visits", align: "right", sortable: true },
                { key: "obs", label: "Observations", align: "right", sortable: true },
                { key: "signed", label: "Signed", align: "right", sortable: true },
                { key: "tasks", label: "Tasks done", align: "right", sortable: true },
                { key: "last", label: "Last active", sortable: true, render: (r: any) => <span className="muted">{r.last ? relTime(r.last) : "—"}</span>, sortValue: (r: any) => r.last || "" },
              ]} />
          </PMCard>

          <PMCard eyebrow="Timeline" icon="clock-outline" count={events.length} className="dash-span2">
            {events.length ? (
              <ol className="dash-timeline">
                {events.map((e) => {
                  const p = people.data.find((x) => x.user_id === e.uid);
                  return (
                    <li key={e.id}>
                      <span className={`dash-tl-dot dash-tl-dot--${e.kind}`} aria-hidden="true" />
                      <PMAvatar name={fullName(p)} size={28} color={avatarColor(e.uid)} />
                      <div className="dash-tl-body">
                        <div><strong>{fullName(p)}</strong> {e.text}{e.where && <> · {e.visitId ? <button className="linkbtn dash-link" onClick={() => openVisit(e.visitId!)}>{e.where}</button> : e.where}</>}</div>
                        <div className="hint">{relTime(e.at)} · {clock(e.at)}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : <p className="hint">Nothing recorded in this period.</p>}
          </PMCard>
        </div>
      )}
    </Page>
  );
}
